import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Input,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../../config";
import Select from "react-select";
import { 
  hasPagePermissionWithAdmin,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin
} from "../../../components/Permission/PermissionService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Eye, Edit, Trash2, Search, Filter, X } from "lucide-react";
import { ClipLoader } from "react-spinners";

// API Helpers
import { getEmployees } from "../../../components/Api/GmsEmployee";
import { getBusinesses } from "../../../components/Api/GmsBusiness";
import { getBranches } from "../../../components/Api/GmsBranch";
import { searchCustomers } from "../../../components/Api/GmsCustomer";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || authUser.user_Id || 1,
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    isAdmin: authUser.isAdmin || false
  };
};

const GmsSoapHistory = (props) => {
  const navigate = useNavigate();
  const session = useMemo(() => getUserSession(), []);
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  
  const canEdit = session.isAdmin || hasEditOrHigherPermission(permissions, '/GmsSoapEntry');

  const [loading, setLoading] = useState(false);
  const [soapList, setSoapList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [staffSearchLoading, setStaffSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [staffSearchTimeout, setStaffSearchTimeout] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    businessId: session.companyId || null,
    branchId: session.branchId || null,
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    customerId: null,
    staffId: null
  });

  const [showFilters, setShowFilters] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedSoap, setSelectedSoap] = useState(null);

  useEffect(() => {
    if (!hasPagePermissionWithAdmin(permissions, '/GmsSoapHistory')) {
      navigate("/Dashboard");
    }
    loadData();
    loadSetupData();
  }, []);

  const loadSetupData = async () => {
    try {
      const [busRes] = await Promise.all([
        getBusinesses({ business_Active: true })
      ]);
      setBusinesses(busRes.data || busRes || []);
      
      if (filters.businessId) {
        loadBranchesData(filters.businessId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadBranchesData = async (busId) => {
    try {
      const res = await getBranches({ Business_Id: busId, branch_Active: true });
      setBranches(res.data || res || []);
      loadEmployeesData(busId, null);
    } catch (e) {
      console.error(e);
    }
  };

  const loadEmployeesData = async (busId, brId) => {
    try {
      const res = await getEmployees({ Employee_Business_Id: busId, Employee_Branch_Id: brId });
      const empList = res.data || res || [];
      setStaffOptions(empList.map(e => ({ value: e.employee_Id, label: e.employee_NameEn })));
    } catch (e) {
      console.error(e);
    }
  };

  const searchCustomersManually = async (inputValue) => {
    const originalInput = inputValue ? inputValue.trim() : '';
    if (!originalInput || originalInput.length < 3) {
      setCustomerOptions([]);
      return;
    }
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(originalInput, 1, 20);
      const items = response.items || response.data?.items || response || [];
      const formattedOptions = (Array.isArray(items) ? items : (items.items || [])).map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
      }));
      setCustomerOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const searchStaffManually = async (inputValue) => {
    const originalInput = inputValue ? inputValue.trim() : '';
    if (!originalInput || originalInput.length < 3) {
      setStaffOptions([]);
      return;
    }
    try {
      setStaffSearchLoading(true);
      const businessId = filters.businessId || null;
      const branchId = filters.branchId || null;
      const response = await searchEmployees(originalInput, 1, 20, businessId, branchId);
      const items = response.items || [];
      const formattedOptions = items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
      }));
      setStaffOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching staff:', error);
      setStaffOptions([]);
    } finally {
      setStaffSearchLoading(false);
    }
  };

  const loadData = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const payload = {
        Business_Id: currentFilters.businessId,
        Branch_Id: currentFilters.branchId,
        FromDate: currentFilters.fromDate,
        ToDate: currentFilters.toDate,
        Customer_Id: currentFilters.customerId?.value,
        Staff_Id: currentFilters.staffId?.value
      };

      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const response = await axios.post(`${BASE_URL}/api/Soap/list`, payload, authHeader);
      
      setSoapList(response.data || []);
    } catch (error) {
      console.error("Error loading SOAP history", error);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (soap) => {
    navigate("/GmsSoapEntry", { state: { soapData: soap } });
  };

  const toggleView = (soap) => {
    setSelectedSoap(soap);
    setViewModal(!viewModal);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="SOAP" breadcrumbItem="Visit History" />

          <Row className="mb-3">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <Button 
                  color="primary" 
                  className="rounded-pill px-4"
                  onClick={() => navigate("/GmsSoapEntry")}
                >
                  <i className="bx bx-plus me-1"></i> New Visit Note
                </Button>
                <Button 
                  color={showFilters ? "secondary" : "info"} 
                  outline 
                  className="rounded-pill px-4"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? <X size={18} className="me-1" /> : <Filter size={18} className="me-1" />}
                  {showFilters ? "Hide Filters" : "Filters"}
                </Button>
              </div>
            </Col>
          </Row>

          {showFilters && (
            <Card className="shadow-sm border-0 mb-4 animated fadeIn">
              <CardBody className="p-4">
                <Row className="g-3">
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">From Date</Label>
                      <Input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">To Date</Label>
                      <Input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">Company</Label>
                      <Select
                        value={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn })).find(o => o.value === filters.businessId)}
                        onChange={(opt) => {
                          setFilters({ ...filters, businessId: opt?.value, branchId: null });
                          loadBranchesData(opt?.value);
                        }}
                        options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                        isDisabled={!!session.companyId}
                        styles={selectStyles}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">Branch</Label>
                      <Select
                        value={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn })).find(o => o.value === filters.branchId)}
                        onChange={(opt) => setFilters({ ...filters, branchId: opt?.value })}
                        options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                        isDisabled={!!session.branchId || !filters.businessId}
                        styles={selectStyles}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">Staff (Name/Code)</Label>
                      <Select
                        value={filters.staffId}
                        onChange={(opt) => setFilters({ ...filters, staffId: opt })}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            if (staffSearchTimeout) clearTimeout(staffSearchTimeout);
                            const timeout = setTimeout(() => {
                              searchStaffManually(inputValue);
                            }, 300);
                            setStaffSearchTimeout(timeout);
                          }
                        }}
                        options={staffOptions}
                        isLoading={staffSearchLoading}
                        styles={selectStyles}
                        placeholder="Search Staff..."
                        filterOption={() => true}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="fw-semibold">Customer (Name/No)</Label>
                      <Select
                        value={filters.customerId}
                        onChange={(opt) => setFilters({ ...filters, customerId: opt })}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            if (searchTimeout) clearTimeout(searchTimeout);
                            const timeout = setTimeout(() => {
                              searchCustomersManually(inputValue);
                            }, 300);
                            setSearchTimeout(timeout);
                          }
                        }}
                        options={customerOptions}
                        isLoading={customerSearchLoading}
                        placeholder="Search Client..."
                        styles={selectStyles}
                        filterOption={() => true}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={12} className="text-end">
                    <Button color="primary" className="px-5 shadow-sm" onClick={() => loadData()}>
                      Apply Search
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table className="table-nowrap align-middle table-hover mb-0">
                  <thead className="bg-light text-muted">
                    <tr>
                      <th className="ps-4">Date</th>
                      <th>Customer</th>
                      <th>Therapist / Staff</th>
                      <th>Details Summary</th>
                      <th className="text-center pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5">
                          <ClipLoader size={30} color="#556ee6" />
                        </td>
                      </tr>
                    ) : soapList.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">No SOAP notes found.</td>
                      </tr>
                    ) : (
                      soapList.map((soap) => (
                        <tr key={soap.soap_Id}>
                          <td className="ps-4 fw-medium text-dark">{formatDate(soap.date)}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar-xs me-2">
                                <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                  {soap.customerName?.charAt(0) || 'C'}
                                </span>
                              </div>
                              <div>
                                <h5 className="font-size-14 mb-0 text-dark">{soap.customerName}</h5>
                                <small className="text-muted">{soap.customerContact || 'No contact'}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge color="soft-info" className="text-info font-size-12 px-2">
                              {soap.staffName}
                            </Badge>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                <small className="text-muted d-block">
                                    <span className="fw-bold">S:</span> {soap.subjective || '-'}
                                </small>
                                <small className="text-muted d-block">
                                    <span className="fw-bold">P:</span> {soap.plan || '-'}
                                </small>
                            </div>
                          </td>
                          <td className="text-center pe-4">
                            <div className="d-flex justify-content-center gap-2">
                              <Button 
                                color="light" 
                                size="sm" 
                                className="btn-rounded text-primary shadow-none border-0"
                                onClick={() => toggleView(soap)}
                                title="View Details"
                              >
                                <Eye size={18} />
                              </Button>
                              {canEdit && (
                                <Button 
                                  color="light" 
                                  size="sm" 
                                  className="btn-rounded text-info shadow-none border-0"
                                  onClick={() => handleEdit(soap)}
                                  title="Edit Note"
                                >
                                  <Edit size={18} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* View SOAP Modal */}
      {/* View SOAP Modal */}
      <Modal 
        isOpen={viewModal} 
        toggle={() => setViewModal(false)} 
        size="lg" 
        centered 
        className="soap-details-modal"
      >
        <div className="modal-header-gradient p-3 d-flex justify-content-between align-items-center">
          <h5 className="modal-title text-white">Visit Note Details</h5>
          <button type="button" className="btn-close btn-close-white" onClick={() => setViewModal(false)}></button>
        </div>
        <ModalBody className="p-4 bg-light bg-opacity-50">
          {selectedSoap && (
            <>
              {/* Header Info Section */}
              <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
                <Row className="align-items-center">
                  <Col md={5} className="d-flex align-items-center">
                    <div className="avatar-md me-3">
                      <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-24">
                        <i className="bx bx-user"></i>
                      </span>
                    </div>
                    <div>
                      <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Client</label>
                      <h5 className="mb-0 text-primary fw-bold">{selectedSoap.customerName}</h5>
                      <span className="text-muted font-size-13">{selectedSoap.customerContact}</span>
                    </div>
                  </Col>
                  <Col md={1} className="d-none d-md-block">
                    <div className="info-divider"></div>
                  </Col>
                  <Col md={3} className="text-center text-md-start">
                    <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Date</label>
                    <div className="d-flex align-items-center justify-content-center justify-content-md-start">
                      <i className="bx bx-calendar-event font-size-18 text-primary me-2"></i>
                      <h6 className="mb-0 fw-semibold">{formatDate(selectedSoap.date)}</h6>
                    </div>
                  </Col>
                  <Col md={1} className="d-none d-md-block">
                    <div className="info-divider"></div>
                  </Col>
                  <Col md={2}>
                    <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Therapist</label>
                    <h6 className="mb-0 text-primary fw-bold">{selectedSoap.staffName || "Alex"}</h6>
                  </Col>
                </Row>
              </div>

              {/* SOAP Content Cards */}
              <div className="soap-cards-container">
                {[
                  { key: 'S', label: 'Subjective', content: selectedSoap.subjective, color: 'primary' },
                  { key: 'O', label: 'Objective', content: selectedSoap.objective, color: 'info' },
                  { key: 'A', label: 'Assessment', content: selectedSoap.assessment, color: 'success' },
                  { key: 'P', label: 'Plan', content: selectedSoap.plan, color: 'warning' }
                ].map((s, idx) => (
                  <Card key={idx} className="border-0 shadow-sm mb-3 rounded-4 overflow-hidden">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className={`soap-letter-icon bg-${s.color} text-white me-3`}>
                          {s.key}
                        </div>
                        <h5 className="mb-0 fw-bold">{s.label}</h5>
                      </div>
                      <div className="border-top pt-3">
                        <p className="text-muted mb-0 lh-base" style={{ fontSize: '0.95rem' }}>
                          {s.content || "No notes provided for this section."}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="border-0 bg-light bg-opacity-50 pb-4 justify-content-center gap-3">
          <Button 
            color="light" 
            onClick={() => setViewModal(false)} 
            className="rounded-pill px-5 py-2 fw-semibold shadow-sm border"
            style={{ minWidth: '160px' }}
          >
            Close View
          </Button>
          {canEdit && selectedSoap && (
            <Button 
              color="primary" 
              onClick={() => { setViewModal(false); handleEdit(selectedSoap); }} 
              className="rounded-pill px-5 py-2 fw-semibold shadow-lg"
              style={{ minWidth: '160px' }}
            >
              Edit Record
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <style>{`
        .soap-details-modal .modal-content {
          border-radius: 20px;
          border: none;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .modal-header-gradient {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
        }
        .info-divider {
          width: 1px;
          height: 40px;
          background-color: #e9ecef;
          margin: 0 auto;
        }
        .soap-letter-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
        }
        .bg-soft-primary {
          background-color: rgba(59, 130, 246, 0.1) !important;
        }
        .lh-base { line-height: 1.6; }
        .animated { animation-duration: 0.3s; animation-fill-mode: both; }
        .fadeIn { animation-name: fadeIn; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </React.Fragment>
  );
};

export default withTranslation()(GmsSoapHistory);
